import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Button, LinearProgress, Paper, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup,
  FormControlLabel, Radio, useMediaQuery, useTheme, CircularProgress,
  Collapse, List, ListItemButton, ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import WmsBarcodeScanner from '../../shared/WmsBarcodeScanner';
import WmsNotification, { useWmsNotification } from '../../shared/WmsNotification';
import { waveService } from '../../services/waveService';
import type { RoutingStrategy, RouteAlternative } from '../../services/waveService';
import PickerNavMap from './PickerNavMap';
import RouteAlternativesBar from './RouteAlternativesBar';
import CancelOrderDialog, { type CancelOrderResult } from './CancelOrderDialog';
import { useTranslation } from 'react-i18next';
import { locationService } from '../../services/locationService';
import { matchesScannedValue, matchesScannedLocation } from '../../shared/scanMatching';

export interface PickListItem {
  orderItemId: string;
  orderId: string;
  orderNumber?: string;
  customerName?: string;
  productId: string;
  productName?: string;
  sku?: string;
  barcode?: string;
  quantity: number;
  pickedQuantity: number;
  locationId?: string;
  locationCode?: string;
  locationName?: string;
  sortOrder: number;
  status: string;
  aisleId?: string;
  aisleName?: string;
  aisleSide?: string;
  gridX?: number;
  gridY?: number;
}

interface PickingActiveFlowProps {
  waveId: string;
  waveNumber: string;
  cartBarcode: string;
  pickList: PickListItem[];
  warehouseId?: string;
  routeStrategy: RoutingStrategy;
  onRouteStrategyChange: (strategy: RoutingStrategy) => void;
  routeAlternatives?: RouteAlternative[];
  onComplete: (pickedCount: number, shortPickCount: number) => void;
  onPickListUpdate: (list: PickListItem[]) => void;
  // Saglanirsa sag ustte "geri" butonu gosterilir (or. tekli listesine geri don).
  onBack?: () => void;
  backLabel?: string;
  cancelledById?: string;
  cancelledByName?: string;
  /** Dalga tamamen bittiyse (tek siparis iptal) ust ekran home'a donebilir. */
  onOrderCancelled?: (result: CancelOrderResult) => void;
}

type PickStep = 'scan-location' | 'scan-product' | 'confirm-quantity';

const isPendingPick = (status?: string) =>
  status !== 'Picked' && status !== 'ShortPick' && status !== 'Sorted' && status !== 'Cancelled';

function normalizeLocationNameFallback(name: string | undefined, rackPrefix: string) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  return raw.replace(/^rack\s*/i, `${rackPrefix} `);
}

interface LocationNodeLite {
  id: string;
  parentId?: string | null;
  type: string;
  code?: string;
  name?: string;
  barcodeData?: string;
}

const PickingActiveFlow: React.FC<PickingActiveFlowProps> = ({
  waveId, waveNumber, cartBarcode, pickList, warehouseId,
  routeStrategy, onRouteStrategyChange, routeAlternatives = [],
  onComplete, onPickListUpdate, onBack, backLabel,
  cancelledById, cancelledByName, onOrderCancelled,
}) => {
  const { t } = useTranslation('warehouse');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(() => {
    const idx = pickList.findIndex(i => i.status !== 'Picked' && i.status !== 'ShortPick' && i.status !== 'Cancelled');
    return idx >= 0 ? idx : 0;
  });
  const [step, setStep] = useState<PickStep>('scan-location');
  // Operatörün fiilen okuttuğu lokasyon (önerilen olmak zorunda değil — backend her
  // geçerli toplanabilir lokasyonu kabul eder). Toplama isteğinde bu ID gönderilir.
  const [scannedLocationId, setScannedLocationId] = useState<string>('');
  const [scannedLocationCode, setScannedLocationCode] = useState<string>('');
  const [quantityInput, setQuantityInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [shortPickOpen, setShortPickOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shortPickMode, setShortPickMode] = useState<'receive' | 'skip' | 'partial'>('skip');
  const [partialQty, setPartialQty] = useState('');
  const [receiveQty, setReceiveQty] = useState('');
  const [locationIndex, setLocationIndex] = useState<Map<string, LocationNodeLite>>(new Map());
  // Lokasyonu olmayan ürün için öneri/atama durumu.
  const [locSuggestion, setLocSuggestion] = useState<{ suggestedLocationId?: string; locationCode?: string; locationName?: string; reason?: string } | null>(null);
  const [locLoading, setLocLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [manualLoc, setManualLoc] = useState('');
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const { notification, notify, clear } = useWmsNotification();

  const currentItem = pickList[currentIndex];
  // Ürünün tanımlı lokasyonu yoksa önce lokasyon atanmalı.
  const needsLocation = !!currentItem && !currentItem.locationId;
  const { pickedCount, shortPickCount, doneCount, activeTotal, progress } = useMemo(() => {
    const active = pickList.filter(i => i.status !== 'Cancelled');
    const picked = active.filter(i => i.status === 'Picked').length;
    const shortPicked = active.filter(i => i.status === 'ShortPick').length;
    const done = picked + shortPicked;
    return {
      pickedCount: picked,
      shortPickCount: shortPicked,
      doneCount: done,
      activeTotal: active.length,
      progress: active.length > 0 ? (done / active.length) * 100 : 0,
    };
  }, [pickList]);

  useEffect(() => {
    if (!warehouseId) {
      setLocationIndex(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res: any = await locationService.getByWarehouse(warehouseId);
        const rows = res?.data?.data || res?.data?.Data || res?.data || [];
        const next = new Map<string, LocationNodeLite>();
        for (const raw of rows) {
          const id = String(raw?.Id || raw?.id || '').trim();
          if (!id) continue;
          next.set(id, {
            id,
            parentId: raw?.ParentId ?? raw?.parentId ?? null,
            type: String(raw?.Type || raw?.type || ''),
            code: String(raw?.Code || raw?.code || '').trim(),
            name: String(raw?.Name || raw?.name || '').trim(),
            barcodeData: String(raw?.BarcodeData || raw?.barcodeData || '').trim(),
          });
        }
        if (!cancelled) setLocationIndex(next);
      } catch {
        if (!cancelled) setLocationIndex(new Map());
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId]);

  // Lokasyonu olmayan ürüne geçildiğinde otomatik lokasyon önerisi getir.
  useEffect(() => {
    if (!currentItem || !needsLocation) { setLocSuggestion(null); return; }
    let cancelled = false;
    setLocLoading(true);
    setLocSuggestion(null);
    waveService.getLocationSuggestion(waveId, currentItem.productId)
      .then(res => { if (!cancelled) setLocSuggestion(res.data?.data || null); })
      .catch(() => { if (!cancelled) setLocSuggestion(null); })
      .finally(() => { if (!cancelled) setLocLoading(false); });
    return () => { cancelled = true; };
  }, [currentItem, needsLocation, waveId]);

  const advanceToNext = useCallback(() => {
    const nextIdx = pickList.findIndex((item, idx) =>
      idx > currentIndex && isPendingPick(item.status)
    );
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setStep('scan-location');
    } else {
      onComplete(pickedCount + (currentItem?.status === 'Picked' ? 0 : 1), shortPickCount);
    }
  }, [pickList, currentIndex, pickedCount, shortPickCount, onComplete, currentItem]);

  const handleOrderCancelled = useCallback((result: CancelOrderResult) => {
    setCancelOpen(false);
    const cancelledOrderId = result.orderId;
    const updated = pickList.map((item) =>
      item.orderId === cancelledOrderId
        ? { ...item, status: 'Cancelled' }
        : item
    );
    onPickListUpdate(updated);

    if (!result.waveStillActive || result.remainingPendingItems <= 0) {
      notify({ type: 'warning', message: t('screens.picking.cancel.successWaveDone'), duration: 2500 });
      if (onOrderCancelled) {
        onOrderCancelled(result);
        return;
      }
      const remaining = updated.filter((item) => isPendingPick(item.status));
      if (remaining.length === 0) {
        onComplete(
          updated.filter(i => i.status === 'Picked').length,
          updated.filter(i => i.status === 'ShortPick').length,
        );
      }
      return;
    }

    notify({ type: 'warning', message: t('screens.picking.cancel.successContinue'), duration: 2000 });
    const nextIdx = updated.findIndex((item, idx) =>
      (idx > currentIndex || item.orderId !== cancelledOrderId) && isPendingPick(item.status)
    );
    const fallbackIdx = updated.findIndex((item) => isPendingPick(item.status));
    const target = nextIdx >= 0 ? nextIdx : fallbackIdx;
    if (target >= 0) {
      setCurrentIndex(target);
      setStep('scan-location');
      setScannedLocationId('');
      setScannedLocationCode('');
    } else {
      onComplete(
        updated.filter(i => i.status === 'Picked').length,
        updated.filter(i => i.status === 'ShortPick').length,
      );
    }
    onOrderCancelled?.(result);
  }, [pickList, onPickListUpdate, currentIndex, notify, t, onOrderCancelled, onComplete]);

  const jumpToItem = useCallback((idx: number) => {
    if (idx < 0 || idx >= pickList.length) return;
    if (!isPendingPick(pickList[idx]?.status)) return;
    setCurrentIndex(idx);
    setStep('scan-location');
    setScannedLocationId('');
    setScannedLocationCode('');
    setListPickerOpen(false);
  }, [pickList]);

  const findPendingIndexByProduct = useCallback((barcode: string) =>
    pickList.findIndex((item) =>
      isPendingPick(item.status) && (
        matchesScannedValue(barcode, item.barcode || '')
        || matchesScannedValue(barcode, item.sku || '')
        || matchesScannedValue(barcode, item.productId || '')
      ),
    ), [pickList]);

  const findPendingIndexByLocation = useCallback((barcode: string, resolvedId: string) =>
    pickList.findIndex((item) =>
      isPendingPick(item.status) && (
        (resolvedId && item.locationId === resolvedId)
        || matchesScannedLocation(barcode, item.locationCode || '')
        || matchesScannedLocation(barcode, item.locationId)
      ),
    ), [pickList]);

  const handleLocationScan = useCallback((barcode: string) => {
    if (!currentItem) return;
    const expected = currentItem.locationCode || '';

    let resolvedId = '';
    let resolvedCode = '';
    if (matchesScannedLocation(barcode, expected) || matchesScannedLocation(barcode, currentItem.locationId)) {
      resolvedId = currentItem.locationId || '';
      resolvedCode = expected;
    } else {
      const match = Array.from(locationIndex.values()).find(
        node => matchesScannedLocation(barcode, node.code)
          || matchesScannedLocation(barcode, node.barcodeData)
          || matchesScannedLocation(barcode, node.name)
          || matchesScannedLocation(barcode, node.id)
      );
      if (match) {
        resolvedId = match.id;
        resolvedCode = match.code || '';
      }
    }

    if (!resolvedId) {
      notify({ type: 'error', message: t('screens.picking.common.locationNotRecognized'), duration: 4000 });
      return;
    }

    const locIdx = findPendingIndexByLocation(barcode, resolvedId);
    if (locIdx >= 0 && locIdx !== currentIndex) {
      jumpToItem(locIdx);
      notify({
        type: 'info',
        message: t('screens.picking.activeFlow.jumpedToLocation', { code: resolvedCode || barcode }),
        duration: 2500,
      });
    }

    setScannedLocationId(resolvedId);
    setScannedLocationCode(resolvedCode);
    setStep('scan-product');

    const active = pickList[locIdx >= 0 ? locIdx : currentIndex];
    if (active?.locationId && resolvedId !== active.locationId) {
      notify({ type: 'info', message: t('screens.picking.common.locationDifferentAccepted', { code: resolvedCode || barcode }), duration: 2500 });
    } else {
      notify({ type: 'success', message: t('screens.picking.common.locationVerified'), duration: 1500 });
    }
  }, [currentItem, currentIndex, locationIndex, pickList, findPendingIndexByLocation, jumpToItem, notify, t]);

  const handleProductScan = (barcode: string) => {
    const productIdx = findPendingIndexByProduct(barcode);
    const targetIdx = productIdx >= 0 ? productIdx : currentIndex;
    const item = pickList[targetIdx];
    if (!item) return;

    if (productIdx >= 0 && productIdx !== currentIndex) {
      jumpToItem(productIdx);
      notify({
        type: 'info',
        message: t('screens.picking.activeFlow.jumpedToProduct', { name: item.productName || item.sku || barcode }),
        duration: 2500,
      });
    }

    const expectedBarcode = item.barcode || '';
    const expectedSku = item.sku || '';
    const expectedProductId = item.productId || '';

    if (
      matchesScannedValue(barcode, expectedBarcode)
      || matchesScannedValue(barcode, expectedSku)
      || matchesScannedValue(barcode, expectedProductId)
    ) {
      if (step !== 'scan-product' && !scannedLocationId && item.locationId) {
        setStep('scan-location');
        notify({
          type: 'info',
          message: t('screens.picking.activeFlow.scanLocationForProduct'),
          duration: 2500,
        });
        return;
      }
      if (item.quantity === 1) {
        handlePickConfirmForIndex(targetIdx, 1);
      } else {
        setCurrentIndex(targetIdx);
        setQuantityInput(String(item.quantity));
        setStep('confirm-quantity');
      }
      notify({ type: 'success', message: t('screens.picking.common.productVerified'), duration: 1500 });
    } else {
      const expectedLabel =
        String(expectedBarcode || expectedSku || expectedProductId || '').trim()
        || (item.productName || t('screens.picking.activeFlow.undefinedProduct'));
      notify({ type: 'error', message: t('screens.picking.common.wrongProduct', { expected: expectedLabel }), duration: 4000 });
    }
  };

  const handlePickConfirmForIndex = useCallback(async (idx: number, qty: number) => {
    const item = pickList[idx];
    if (!item || saving) return;
    setSaving(true);
    try {
      await waveService.pickItem(waveId, {
        OrderItemId: item.orderItemId,
        PickedQuantity: qty,
        ScannedLocationId: scannedLocationId || item.locationId,
      });
      const updated = [...pickList];
      updated[idx] = { ...updated[idx], pickedQuantity: qty, status: 'Picked' };
      onPickListUpdate(updated);

      notify({ type: 'success', message: t('screens.picking.common.pickedToast', { done: doneCount + 1, total: pickList.length }), duration: 2000 });

      const remaining = updated.findIndex((it, i) => i > idx && isPendingPick(it.status));
      if (remaining >= 0) {
        setCurrentIndex(remaining);
        setStep('scan-location');
        setScannedLocationId('');
        setScannedLocationCode('');
      } else {
        const anyLeft = updated.some(i => isPendingPick(i.status));
        if (!anyLeft) {
          onComplete(
            updated.filter(i => i.status === 'Picked').length,
            updated.filter(i => i.status === 'ShortPick').length,
          );
        } else {
          const firstLeft = updated.findIndex(i => isPendingPick(i.status));
          setCurrentIndex(firstLeft);
          setStep('scan-location');
          setScannedLocationId('');
          setScannedLocationCode('');
        }
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.pickSaveFailed') });
    }
    setSaving(false);
  }, [pickList, saving, waveId, scannedLocationId, onPickListUpdate, onComplete, doneCount, notify, t]);

  const handlePickConfirm = useCallback(async (qty: number) => {
    await handlePickConfirmForIndex(currentIndex, qty);
  }, [currentIndex, handlePickConfirmForIndex]);

  const handleShortPick = useCallback(async () => {
    if (!currentItem || saving) return;
    setSaving(true);
    try {
      const partial = shortPickMode === 'partial' ? parseInt(partialQty) || 0 : 0;
      await waveService.shortPickItem(waveId, {
        OrderItemId: currentItem.orderItemId,
        PartialQuantity: partial > 0 ? partial : undefined,
        Reason: shortPickMode === 'skip' ? t('screens.picking.activeFlow.reasonSkip') : t('screens.picking.activeFlow.reasonPartial'),
      });
      const updated = [...pickList];
      updated[currentIndex] = { ...updated[currentIndex], pickedQuantity: partial, status: 'ShortPick' };
      onPickListUpdate(updated);
      setShortPickOpen(false);

      notify({ type: 'warning', message: t('screens.picking.common.shortPickSaved'), duration: 2000 });

      const remaining = updated.findIndex((item, idx) =>
        idx > currentIndex && item.status !== 'Picked' && item.status !== 'ShortPick'
      );
      if (remaining >= 0) {
        setCurrentIndex(remaining);
        setStep('scan-location');
      } else {
        const anyLeft = updated.some(i => i.status !== 'Picked' && i.status !== 'ShortPick');
        if (!anyLeft) {
          onComplete(
            updated.filter(i => i.status === 'Picked').length,
            updated.filter(i => i.status === 'ShortPick').length
          );
        }
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.shortPickSaveFailed') });
    }
    setSaving(false);
  }, [currentItem, currentIndex, waveId, pickList, shortPickMode, partialQty, onPickListUpdate, onComplete, saving, notify, t]);

  // Önerilen (veya manuel seçilen) lokasyona lokasyonsuz stoğu ata.
  const handleAssignLocation = useCallback(async (locationId?: string, locationCode?: string) => {
    if (!currentItem || assigning) return;
    if (!locationId) {
      notify({ type: 'error', message: t('screens.picking.common.noLocationToAssign') });
      return;
    }
    setAssigning(true);
    try {
      const res = await waveService.assignLocation(waveId, {
        ProductId: currentItem.productId,
        LocationId: locationId,
      });
      const data = res.data?.data;
      if (data?.assigned) {
        // Kalemi yeni lokasyonuyla güncelle; artık normal okutma akışı çalışır.
        const updated = [...pickList];
        updated[currentIndex] = {
          ...currentItem,
          locationId,
          locationCode: data.locationCode || locationCode || currentItem.locationCode,
          locationName: data.locationName || currentItem.locationName,
        };
        onPickListUpdate(updated);
        notify({ type: 'success', message: t('screens.picking.common.locationAssigned', { code: data.locationCode || locationCode || '' }), duration: 2000 });
        setStep('scan-location');
      } else if (data?.requiresReceiving) {
        // Taşınacak stok yok → yerinde mal kabul akışına yönlendir.
        notify({ type: 'info', message: t('screens.picking.common.requiresReceiving'), duration: 3000 });
        setShortPickMode('receive');
        setReceiveQty(String(currentItem.quantity));
        setShortPickOpen(true);
      } else {
        notify({ type: 'warning', message: data?.message || t('screens.picking.common.assignFailedSoft') });
      }
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.assignFailed') });
    }
    setAssigning(false);
  }, [currentItem, currentIndex, waveId, pickList, assigning, onPickListUpdate, notify, t]);

  // Okutulan/girilen lokasyon kodunu bilinen lokasyonlarla eşleştirip atamayı tetikler.
  const handleManualAssign = useCallback((rawCode: string) => {
    const code = String(rawCode || '').trim();
    if (!code) return;
    let matchId = '';
    let matchCode = '';
    const match = Array.from(locationIndex.values()).find(
      node => matchesScannedLocation(code, node.code)
        || matchesScannedLocation(code, node.barcodeData)
        || matchesScannedLocation(code, node.name)
        || matchesScannedLocation(code, node.id)
    );
    if (match) {
      matchId = match.id;
      matchCode = match.code || '';
    }
    if (!matchId) {
      notify({ type: 'error', message: t('screens.picking.activeFlow.locationNotFound', { code }), duration: 4000 });
      return;
    }
    setManualLoc('');
    handleAssignLocation(matchId, matchCode);
  }, [locationIndex, handleAssignLocation, notify, t]);

  // Toplama sırasında yerinde hızlı stok girişi (mini mal kabul).
  const handleReceiveStock = useCallback(async () => {
    if (!currentItem || saving) return;
    const targetLoc = currentItem.locationId || locSuggestion?.suggestedLocationId;
    const targetCode = currentItem.locationCode || locSuggestion?.locationCode;
    if (!targetLoc) {
      notify({ type: 'error', message: t('screens.picking.common.noStockLocation') });
      return;
    }
    const qty = parseInt(receiveQty) || 0;
    if (qty <= 0) {
      notify({ type: 'error', message: t('screens.picking.common.qtyMustBePositive') });
      return;
    }
    setSaving(true);
    try {
      await waveService.adjustStock(waveId, {
        ProductId: currentItem.productId,
        LocationId: targetLoc,
        Quantity: qty,
        Reason: t('screens.picking.common.adjustReason'),
      });
      // Stok girildi; kalemi hedef lokasyona bağla ve normal okutma akışına dön.
      const updated = [...pickList];
      updated[currentIndex] = {
        ...currentItem,
        locationId: targetLoc,
        locationCode: targetCode || currentItem.locationCode,
      };
      onPickListUpdate(updated);
      setShortPickOpen(false);
      notify({ type: 'success', message: t('screens.picking.activeFlow.receiveStockOk'), duration: 2500 });
      setStep('scan-location');
    } catch (err: any) {
      notify({ type: 'error', message: err?.response?.data?.message || t('screens.picking.common.stockSaveFailed') });
    }
    setSaving(false);
  }, [currentItem, currentIndex, waveId, pickList, receiveQty, locSuggestion, saving, onPickListUpdate, notify, t]);

  const mapHeight = isMobile ? 168 : 200;
  const normalizeAisleName = useCallback((name?: string) => {
    const raw = String(name || '').trim();
    if (!raw) return '';
    return raw.replace(/^aisle\s*/i, `${t('screens.picking.activeFlow.aislePrefix')} `);
  }, [t]);

  /** Harita üzerinde gösterilecek kısa konum özeti (barkod chip'inde zaten kod var). */
  const locationSubtitle = useMemo(() => {
    if (!currentItem) return '';
    const typeEq = (nodeType: string | undefined, expected: string) =>
      String(nodeType || '').toLowerCase() === expected.toLowerCase();
    const rackPrefix = t('screens.picking.activeFlow.rackPrefix');
    const toRackText = (raw?: string) => {
      const src = String(raw || '').trim();
      if (!src) return '';
      const m = src.match(/^R\s*[-_]?\s*(\d+)$/i);
      if (m) return `${rackPrefix} ${m[1]}`;
      return src.replace(/^rack\s*/i, `${rackPrefix} `);
    };

    let aisleNode: LocationNodeLite | undefined;
    let rackNode: LocationNodeLite | undefined;
    let cursor = currentItem.locationId ? locationIndex.get(currentItem.locationId) : undefined;
    const guard = new Set<string>();
    while (cursor && !guard.has(cursor.id)) {
      guard.add(cursor.id);
      if (!aisleNode && typeEq(cursor.type, 'Aisle')) aisleNode = cursor;
      if (!rackNode && typeEq(cursor.type, 'Rack')) rackNode = cursor;
      const pid = cursor.parentId ? String(cursor.parentId) : '';
      cursor = pid ? locationIndex.get(pid) : undefined;
    }

    const codeParts = String(currentItem.locationCode || '').split('-').map((x) => x.trim()).filter(Boolean);
    const rackCode = rackNode?.code || codeParts[1] || currentItem.locationName || '';
    const aisleRaw = aisleNode?.name || aisleNode?.code || currentItem.aisleName || '';
    const rackText = toRackText(rackCode) || normalizeLocationNameFallback(currentItem.locationName, rackPrefix);
    const aisleText = aisleRaw ? normalizeAisleName(aisleRaw) : '';
    return [rackText, aisleText].filter(Boolean).join(' · ');
  }, [currentItem, locationIndex, normalizeAisleName, t]);

  if (!currentItem) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <Typography variant="h6">{t('screens.picking.activeFlow.allPicked')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <WmsNotification notification={notification} onClose={clear} />

      {/* Progress Bar */}
      <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'background.paper', flexShrink: 0 }}>
        <LinearProgress variant="determinate" value={progress}
          sx={{ flex: 1, height: 8, borderRadius: 4 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('screens.picking.common.progressCount', { done: doneCount, total: activeTotal })}
        </Typography>
        {cartBarcode && (
          <Chip size="small" label={t('screens.picking.common.cartLabel', { code: cartBarcode })} variant="outlined" sx={{ height: 24 }} />
        )}
        {onBack && (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ flexShrink: 0, color: 'text.secondary', minHeight: 32 }}
          >
            {backLabel || t('screens.picking.common.back')}
          </Button>
        )}
      </Box>

      {/* Sprint 2 Görev 3: manuel mod — listeden ürün/konum seçimi */}
      <Box sx={{ px: 1.5, pb: 0.5, bgcolor: 'background.paper', flexShrink: 0 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FormatListBulletedIcon />}
          endIcon={listPickerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setListPickerOpen(v => !v)}
          sx={{ minHeight: 32 }}
        >
          {t('screens.picking.activeFlow.listPickerTitle')}
        </Button>
        <Collapse in={listPickerOpen}>
          <Paper variant="outlined" sx={{ mt: 0.75, maxHeight: 180, overflow: 'auto' }}>
            <List dense disablePadding>
              {pickList.map((item, idx) => {
                const done = !isPendingPick(item.status);
                const isCurrent = idx === currentIndex;
                return (
                  <ListItemButton
                    key={item.orderItemId}
                    selected={isCurrent}
                    disabled={done}
                    onClick={() => jumpToItem(idx)}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={isCurrent ? 700 : 500} noWrap>
                          {item.locationCode || item.locationName || t('screens.picking.common.undefinedLocation')}
                          {' · '}
                          {item.productName || t('screens.picking.common.productFallback')}
                        </Typography>
                      }
                      secondary={done
                        ? t('screens.picking.single.done')
                        : t('screens.picking.common.quantityValue', { count: item.quantity })}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Collapse>
      </Box>

      {/* Main Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1.5, pt: 1, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {warehouseId && (
          <>
            <RouteAlternativesBar
              alternatives={routeAlternatives}
              selectedStrategy={routeStrategy}
              onSelect={onRouteStrategyChange}
            />
            <PickerNavMap
              warehouseId={warehouseId}
              pickList={pickList}
              currentIndex={currentIndex}
              height={mapHeight}
              locationSubtitle={locationSubtitle}
              ghostStops={(routeAlternatives.find(a => a.stopOrder?.length)?.stopOrder || [])
                .filter(s => s.gridX != null && s.gridY != null)
                .map(s => ({ gridX: s.gridX as number, gridY: s.gridY as number }))}
            />
          </>
        )}

        {/* Product Card — kompakt */}
        <Paper variant="outlined" sx={{ p: 1.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
            {t('screens.picking.common.toBePicked')}
          </Typography>
          <Typography sx={{ fontWeight: 700, mt: 0.25, fontSize: '0.95rem', lineHeight: 1.25 }}>
            {currentItem.productName || t('screens.picking.common.productFallback')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('screens.picking.common.barcodeValue', { value: currentItem.barcode || currentItem.sku || '-' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.35 }}>
            {t('screens.picking.common.quantityValue', { count: currentItem.quantity })}
          </Typography>
        </Paper>

        {/* Lokasyonu olmayan ürün: önce lokasyon ata */}
        {needsLocation && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.main', bgcolor: 'rgba(251,140,0,0.06)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AddLocationAltIcon color="warning" fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t('screens.picking.common.noLocationDefined')}
              </Typography>
            </Box>

            {locLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">{t('screens.picking.activeFlow.suggestionLoading')}</Typography>
              </Box>
            ) : locSuggestion?.suggestedLocationId ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{t('screens.picking.activeFlow.suggestedLocationLabel')}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {locSuggestion.locationName || locSuggestion.locationCode}
                </Typography>
                {locSuggestion.reason && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {locSuggestion.reason}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="warning"
                  disabled={assigning}
                  onClick={() => handleAssignLocation(locSuggestion?.suggestedLocationId, locSuggestion?.locationCode)}
                  sx={{ mt: 0.5 }}
                >
                  {assigning ? '...' : t('screens.picking.common.assignLocationTo', { code: locSuggestion.locationCode })}
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                {locSuggestion?.reason || t('screens.picking.activeFlow.noAutoSuggestion')}
              </Typography>
            )}

            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('screens.picking.activeFlow.manualHint')}
              </Typography>
              <WmsBarcodeScanner
                onScan={handleManualAssign}
                placeholder={t('screens.picking.common.scanLocationPlaceholder')}
                size="medium"
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('screens.picking.activeFlow.manualCodePlaceholder')}
                  value={manualLoc}
                  onChange={e => setManualLoc(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleManualAssign(manualLoc); }}
                />
                <Button variant="outlined" disabled={assigning || !manualLoc.trim()} onClick={() => handleManualAssign(manualLoc)}>
                  {t('screens.picking.activeFlow.assign')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Button
                variant="text"
                color="primary"
                startIcon={<WarningIcon />}
                onClick={() => { setShortPickMode('receive'); setReceiveQty(String(currentItem.quantity)); setShortPickOpen(true); }}
              >
                {t('screens.picking.activeFlow.receiveInPlace')}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {/* Barkod okutma — her zaman görünür (scroll dışında) */}
      {!needsLocation && step === 'scan-location' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {t('screens.picking.common.scanLocationStep')}
          </Typography>
          <WmsBarcodeScanner
            onScan={handleLocationScan}
            placeholder={t('screens.picking.common.scanLocationPlaceholder')}
            size="medium"
          />
        </Box>
      )}

      {!needsLocation && step === 'scan-product' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              {t('screens.picking.common.locationVerifiedAt', { code: scannedLocationCode || currentItem.locationCode })}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {t('screens.picking.common.scanProductStep')}
          </Typography>
          <WmsBarcodeScanner
            onScan={handleProductScan}
            placeholder={t('screens.picking.common.scanProductPlaceholder')}
            size="medium"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: 'block' }}>
            {t('screens.picking.common.expectedWithName', { value: currentItem.barcode || currentItem.sku || '-', name: currentItem.productName })}
          </Typography>
        </Box>
      )}

      {!needsLocation && step === 'confirm-quantity' && (
        <Box sx={{ flexShrink: 0, px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
            <Typography variant="caption" color="success.main">{t('screens.picking.common.productVerifiedShort')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 700 }}>
            {t('screens.picking.activeFlow.qty3Confirm', { count: currentItem.quantity })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              type="number"
              value={quantityInput}
              onChange={e => setQuantityInput(e.target.value)}
              sx={{ width: 100 }}
              inputProps={{ min: 1, max: currentItem.quantity }}
            />
            <Button
              variant="contained"
              onClick={() => handlePickConfirm(parseInt(quantityInput) || currentItem.quantity)}
              disabled={saving}
              sx={{ height: 40 }}
            >
              {saving ? '...' : t('common.confirm')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Bottom Actions — tek satır, eşit genişlik, responsive */}
      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: 1,
          pt: 1,
          display: 'flex',
          gap: { xs: 0.75, sm: 1 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<SkipNextIcon />}
          onClick={() => advanceToNext()}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          {t('screens.picking.common.skip')}
        </Button>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<WarningIcon />}
          onClick={() => { setShortPickOpen(true); setShortPickMode(needsLocation ? 'skip' : 'receive'); setPartialQty(''); setReceiveQty(String(currentItem.quantity)); }}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          {t('screens.picking.common.stockIssue')}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelOutlinedIcon />}
          onClick={() => setCancelOpen(true)}
          sx={{
            flex: 1,
            minWidth: 0,
            px: { xs: 0.5, sm: 1.5 },
            fontSize: { xs: '0.72rem', sm: '0.875rem' },
            '& .MuiButton-startIcon': { mr: { xs: 0.35, sm: 0.75 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {t('screens.picking.cancel.button')}
          </Box>
          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
            {t('common.cancel')}
          </Box>
        </Button>
      </Box>

      {/* Stok Sorunu Dialog */}
      <Dialog open={shortPickOpen} onClose={() => setShortPickOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('screens.picking.common.stockIssue')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('screens.picking.common.stockIssueProduct')} <strong>{currentItem.productName}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('screens.picking.common.stockIssueLocation')} <strong>{currentItem.locationCode || locSuggestion?.locationCode || t('screens.picking.common.undefinedLocation')}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('screens.picking.common.expectedLabel')} <strong>{t('screens.picking.common.piecesValue', { count: currentItem.quantity })}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('screens.picking.common.whatToDo')}</Typography>
          <RadioGroup value={shortPickMode} onChange={e => setShortPickMode(e.target.value as any)}>
            <FormControlLabel value="receive" control={<Radio />}
              label={t('screens.picking.activeFlow.modeReceive')} />
            <FormControlLabel value="partial" control={<Radio />}
              label={t('screens.picking.activeFlow.modePartial')} />
            <FormControlLabel value="skip" control={<Radio />}
              label={t('screens.picking.activeFlow.modeSkip')} />
          </RadioGroup>
          {shortPickMode === 'receive' && (
            <>
              {!(currentItem.locationId || locSuggestion?.suggestedLocationId) && (
                <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
                  {t('screens.picking.activeFlow.noLocationForStock')}
                </Typography>
              )}
              <TextField
                size="small"
                type="number"
                label={t('screens.picking.activeFlow.receiveQtyLabel')}
                value={receiveQty}
                onChange={e => setReceiveQty(e.target.value)}
                sx={{ mt: 1 }}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </>
          )}
          {shortPickMode === 'partial' && (
            <TextField
              size="small"
              type="number"
              label={t('screens.picking.activeFlow.partialQtyLabel')}
              value={partialQty}
              onChange={e => setPartialQty(e.target.value)}
              sx={{ mt: 1 }}
              inputProps={{ min: 0, max: currentItem.quantity - 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortPickOpen(false)}>{t('common.cancel')}</Button>
          {shortPickMode === 'receive' ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleReceiveStock}
              disabled={saving || !(currentItem.locationId || locSuggestion?.suggestedLocationId)}
            >
              {saving ? '...' : t('screens.picking.activeFlow.enterStock')}
            </Button>
          ) : (
            <Button variant="contained" color="warning" onClick={handleShortPick} disabled={saving}>
              {saving ? '...' : t('common.confirm')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CancelOrderDialog
        open={cancelOpen}
        orderId={currentItem.orderId}
        orderNumber={currentItem.orderNumber}
        productName={currentItem.productName}
        cancelledById={cancelledById}
        cancelledByName={cancelledByName}
        onClose={() => setCancelOpen(false)}
        onCancelled={handleOrderCancelled}
      />
    </Box>
  );
};

export default PickingActiveFlow;
